/**
 * Settings > Notifications (tracker item 35, ADMIN only; backend
 * requireRole('ADMIN')).
 *
 * 1. Provider connection: a provider picker and a form generated from the
 *    chosen adapter's configSchema, so this page has no provider-specific
 *    code. Secret fields are password inputs that show the stored hint and
 *    are only sent when changed. Save, then Verify.
 * 2. Sending: master switch (only once the connection is VERIFIED), sender
 *    name. (The test email, to any typed address, lives in section 1.)
 * 3. Messages: every message type (grouped), with its on/off status, each
 *    linking to its own page (NotificationTemplatePage) where the template
 *    is edited and the HTML email previewed.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, INotificationsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { ProviderStatusBadge, TemplateStatusBadge } from '../../components/NotificationBadges';
import {
  buildProviderSaveRequest,
  groupMessageTemplates,
  messageStatusText,
  missingProviderFields,
  notificationTemplatePath,
  providerFormValues,
} from './notificationUi';
import { Toast } from '../../components/Toast';

export function NotificationSettingsPage() {
  const api: INotificationsApi = useMemo(() => createAPIClient(), []);
  const [providers, setProviders] = useState<types.NotificationProviderDescriptor[]>([]);
  const [config, setConfig] = useState<types.NotificationProviderConfig | null>(null);
  const [settings, setSettings] = useState<types.NotificationSettings | null>(null);
  const [templates, setTemplates] = useState<types.NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [p, c, s, t] = await Promise.all([
      api.listNotificationProviders(),
      api.getNotificationProvider(),
      api.getNotificationSettings(),
      api.listNotificationTemplates(),
    ]);
    setProviders(p);
    setConfig(c);
    setSettings(s);
    setTemplates(t);
  }, [api]);

  useEffect(() => {
    load()
      .catch((cause) => setError(getErrorMessage(cause, 'Could not load notification settings')))
      .finally(() => setLoading(false));
  }, [load]);

  if (loading) return <main className="max-w-5xl mx-auto px-4 py-6 sm:py-8">Loading notification settings…</main>;

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/settings" className="text-sm text-blue-700 hover:underline">← Settings</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Notifications</h1>
        </div>
        <Link to="/settings/notifications/history" className={`${secondaryButtonClass} inline-flex items-center`}>Message history</Link>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {settings?.deliveryMode === 'log' && (
        <p role="note" className="rounded bg-yellow-50 p-3 text-sm text-yellow-900">
          This server is in log-only mode: messages are written to the server log and no email leaves the system.
        </p>
      )}
      <ProviderSection
        api={api}
        providers={providers}
        config={config}
        onSaved={(saved) => {
          setConfig(saved);
          setSettings((s) => (s ? { ...s, providerStatus: saved.status } : s));
        }}
      />
      {settings && <SendingSection api={api} settings={settings} onSaved={setSettings} />}
      <MessagesSection templates={templates} />
    </main>
  );
}

function ProviderSection({
  api,
  providers,
  config,
  onSaved,
}: {
  api: INotificationsApi;
  providers: types.NotificationProviderDescriptor[];
  config: types.NotificationProviderConfig | null;
  onSaved: (config: types.NotificationProviderConfig) => void;
}) {
  const [providerId, setProviderId] = useState(config?.provider ?? providers[0]?.id ?? '');
  const provider = providers.find((p) => p.id === providerId) ?? null;
  const [values, setValues] = useState<Record<string, string>>(() => (provider ? providerFormValues(provider, config) : {}));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [testAddress, setTestAddress] = useState('');

  function pick(id: string) {
    setProviderId(id);
    const next = providers.find((p) => p.id === id);
    setValues(next ? providerFormValues(next, config) : {});
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not update the connection'));
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(async () => {
      if (!provider) return;
      const missing = missingProviderFields(provider, values, config);
      if (missing.length) throw new Error(`Required: ${missing.join(', ')}`);
      const saved = await api.saveNotificationProvider(buildProviderSaveRequest(provider, values));
      onSaved(saved);
      setValues(providerFormValues(provider, saved));
      setNotice('Connection saved. Verify it before sending.');
    });

  const verify = () =>
    run(async () => {
      const result = await api.verifyNotificationProvider();
      onSaved(result.config);
      if (result.ok) setNotice(result.message || 'Connection verified.');
      else setError(result.message || 'Verification failed.');
    });

  const sameProvider = config?.provider === providerId;
  const verified = sameProvider && config?.status === 'VERIFIED';

  const sendTest = () =>
    run(async () => {
      const result = await api.sendTestNotification(testAddress.trim());
      const to = result.message.toAddress ?? testAddress.trim();
      setNotice(
        result.deduplicated
          ? `A test message to ${to} was already queued in the last minute.`
          : `Test email to ${to}: ${messageStatusText(result.message)}.`
      );
    });

  return (
    <section className="bg-white rounded-lg shadow p-4 sm:p-5" aria-labelledby="provider-heading">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 id="provider-heading" className="font-semibold text-lg">Provider connection</h2>
        <ProviderStatusBadge status={sameProvider ? config?.status ?? null : null} />
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      {sameProvider && config?.status === 'FAILED' && config.lastError && (
        <p className="mb-3 text-sm text-red-700 break-words">Last check: {config.lastError}</p>
      )}
      {providers.length === 0 ? (
        <p className="text-gray-600">No email providers are available on this server.</p>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div className="sm:col-span-2">
            <label htmlFor="provider-picker" className={labelClass}>Provider</label>
            <select id="provider-picker" className={inputClass} value={providerId} onChange={(e) => pick(e.target.value)} disabled={busy}>
              {providers.map((p) => <option key={p.id} value={p.id}>{p.displayName}</option>)}
            </select>
            {provider?.setupHelp && <p className="mt-2 text-sm text-gray-600">{provider.setupHelp}</p>}
          </div>
          {provider?.configSchema.map((field) => {
            const id = `provider-field-${field.key}`;
            const hint = field.secret && sameProvider && config?.hasCredentials
              ? field.required
                ? `Saved (${config.credentialsHint ?? '****'}). Leave blank to keep it.`
                : 'Leave blank to keep the saved value.'
              : null;
            return (
              <div key={field.key} className="min-w-0">
                <label htmlFor={id} className={labelClass}>
                  {field.label}
                  {field.required && <span className="ml-0.5 text-red-600">*</span>}
                </label>
                {field.type === 'select' ? (
                  <select id={id} className={inputClass} value={values[field.key] ?? ''} disabled={busy}
                    onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}>
                    {(field.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input
                    id={id}
                    type={field.secret ? 'password' : field.type === 'email' ? 'email' : 'text'}
                    autoComplete={field.secret ? 'new-password' : 'off'}
                    className={inputClass}
                    value={values[field.key] ?? ''}
                    disabled={busy}
                    placeholder={hint ? '••••••••' : undefined}
                    onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                  />
                )}
                {(hint || field.help) && <p className="mt-1 text-xs text-gray-500">{hint ?? field.help}</p>}
              </div>
            );
          })}
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <button type="submit" className={primaryButtonClass} disabled={busy || !provider}>Save connection</button>
            <button type="button" className={secondaryButtonClass} disabled={busy || !sameProvider} onClick={() => void verify()}>
              Verify connection
            </button>
          </div>
        </form>
      )}
      {provider && (
        <form
          className="mt-5 pt-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void sendTest();
          }}
        >
          <div className="flex-1 min-w-0">
            <label htmlFor="test-email-address" className={labelClass}>Send a test email to</label>
            <input
              id="test-email-address"
              type="email"
              className={inputClass}
              value={testAddress}
              maxLength={320}
              autoComplete="off"
              placeholder="name@example.com"
              disabled={busy}
              onChange={(e) => setTestAddress(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              {verified ? 'Sends a short message marked as a test.' : 'Verify the connection to send a test email.'}
            </p>
          </div>
          <button type="submit" className={secondaryButtonClass} disabled={busy || !verified || !testAddress.trim()}>
            Send test email
          </button>
        </form>
      )}
    </section>
  );
}

function SendingSection({
  api,
  settings,
  onSaved,
}: {
  api: INotificationsApi;
  settings: types.NotificationSettings;
  onSaved: (settings: types.NotificationSettings) => void;
}) {
  const [fromName, setFromName] = useState(settings.fromName ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const verified = settings.providerStatus === 'VERIFIED';

  async function run(action: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not save'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white rounded-lg shadow p-4 sm:p-5" aria-labelledby="sending-heading">
      <h2 id="sending-heading" className="font-semibold text-lg mb-4">Sending</h2>
      {error && <Toast kind="error">{error}</Toast>}
      {notice && <Toast kind="success">{notice}</Toast>}
      <label className="flex items-center gap-2 min-h-[44px] sm:min-h-0 text-sm font-medium text-gray-800">
        <input
          type="checkbox"
          checked={settings.enabled}
          disabled={busy || (!verified && !settings.enabled)}
          onChange={(e) =>
            void run(async () => {
              const saved = await api.updateNotificationSettings({ enabled: e.target.checked });
              onSaved(saved);
              return saved.enabled ? 'Notifications are on.' : 'Notifications are off. Nothing will be sent.';
            })
          }
        />
        Send notifications
      </label>
      {!verified && <p className="mt-1 text-xs text-gray-500">Verify the provider connection to turn sending on.</p>}
      <form
        className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void run(async () => {
            const saved = await api.updateNotificationSettings({ fromName: fromName.trim() || null });
            onSaved(saved);
            return 'Sender name saved.';
          });
        }}
      >
        <div className="flex-1 min-w-0">
          <label htmlFor="notification-from-name" className={labelClass}>Sender name</label>
          <input id="notification-from-name" className={inputClass} value={fromName} maxLength={200} disabled={busy}
            placeholder={settings.effectiveFromName} onChange={(e) => setFromName(e.target.value)} />
          <p className="mt-1 text-xs text-gray-500 break-words">
            Blank uses the company name. Replies go to {settings.replyTo || 'the company email (Settings > Company)'}.
          </p>
        </div>
        <button type="submit" className={secondaryButtonClass} disabled={busy}>Save sender name</button>
      </form>
    </section>
  );
}

function MessagesSection({ templates }: { templates: types.NotificationTemplate[] }) {
  const groups = groupMessageTemplates(templates);
  return (
    <section className="bg-white rounded-lg shadow p-4 sm:p-5" aria-labelledby="messages-heading">
      <h2 id="messages-heading" className="font-semibold text-lg mb-1">Messages</h2>
      <p className="text-sm text-gray-600 mb-4">
        Each message type has its own page to edit the subject and text, turn it on or off, and preview the email.
      </p>
      {groups.length === 0 ? (
        <p className="text-sm text-gray-500">No message types are available.</p>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.id}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{group.title}</h3>
              <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                {group.templates.map((template) => (
                  <li key={template.eventKey}>
                    <Link
                      to={notificationTemplatePath(template.eventKey)}
                      className="flex items-center gap-3 px-3 sm:px-4 py-3 min-h-[44px] hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-gray-900">{template.label}</span>
                        <span className="block text-sm text-gray-600">{template.description}</span>
                      </span>
                      <TemplateStatusBadge active={template.isActive} />
                      <span aria-hidden="true" className="text-gray-400">›</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
